import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GripVertical, Phone, Monitor, Users, CheckSquare, FileText } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { confirmAsync } from '@/lib/confirm';

export default function Settings({ user }) {
  // CRM Statuses
  const [statuses, setStatuses] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);

  // Action Types
  const [actionTypes, setActionTypes] = useState([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [editingActionType, setEditingActionType] = useState(null);

  // Ticket Categories
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Departments
  const [departments, setDepartments] = useState([]);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);

  // Teamup Categories
  const [teamupCategories, setTeamupCategories] = useState([]);
  const [teamupCategoryDialogOpen, setTeamupCategoryDialogOpen] = useState(false);
  const [editingTeamupCategory, setEditingTeamupCategory] = useState(null);

  useEffect(() => {
    fetchStatuses();
    fetchActionTypes();
    fetchCategories();
    fetchDepartments();
    fetchTeamupCategories();
  }, []);

  // ============= CRM STATUSES =============
  const fetchStatuses = async () => {
    try {
      const response = await axiosInstance.get('/crm-statuses');
      setStatuses(response.data);
    } catch (error) {
      toast.error('Error al cargar estados');
    }
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = (formData.get('name') || '').trim();
    let key = (formData.get('key') || '').trim().toLowerCase();
    if (!key && name) {
      key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    }
    if (!editingStatus && statuses.some(s => s.key === key && s.id !== editingStatus?.id)) {
      toast.error(`Ya existe un estado con la clave "${key}". Cambia la clave por una única.`);
      return;
    }
    const data = {
      name,
      key,
      description: formData.get('description'),
      color: formData.get('color'),
      convert_to_customer: formData.get('convert_to_customer') === 'on',
      create_support_ticket: formData.get('create_support_ticket') === 'on',
      order: editingStatus ? editingStatus.order : statuses.length + 1
    };

    try {
      if (editingStatus) {
        await axiosInstance.put(`/crm-statuses/${editingStatus.id}`, data);
        toast.success('Estado actualizado');
      } else {
        await axiosInstance.post('/crm-statuses', data);
        toast.success('Estado creado');
      }
      setStatusDialogOpen(false);
      setEditingStatus(null);
      fetchStatuses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = error.response?.status === 409 && detail ? detail : (detail || error.message);
      console.error('Error al guardar estado:', detail || error.message);
      toast.error(`Error al guardar estado: ${message}`);
    }
  };

  const handleDeleteStatus = async (id) => {
    if (!confirm('¿Estás seguro de eliminar este estado?')) return;
    
    try {
      await axiosInstance.delete(`/crm-statuses/${id}`);
      toast.success('Estado eliminado');
      fetchStatuses();
    } catch (error) {
      toast.error('Error al eliminar estado');
    }
  };

  const openEditStatusDialog = (status) => {
    setEditingStatus(status);
    setStatusDialogOpen(true);
  };

  const openCreateStatusDialog = () => {
    setEditingStatus(null);
    setStatusDialogOpen(true);
  };

  const handleStatusDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));
    
    setStatuses(updatedItems);
    
    // Save new order to backend
    try {
      const reorderData = updatedItems.map(s => ({ id: s.id, order: s.order }));
      await axiosInstance.post('/crm-statuses/reorder', reorderData);
      toast.success('Orden actualizado');
    } catch (error) {
      console.error('Error al actualizar orden:', error);
      toast.error('Error al actualizar orden: ' + (error.response?.data?.detail || error.message));
      fetchStatuses(); // Revert on error
    }
  };

  // ============= ACTION TYPES =============
  const fetchActionTypes = async () => {
    try {
      const response = await axiosInstance.get('/action-types');
      setActionTypes(response.data);
    } catch (error) {
      toast.error('Error al cargar tipos de acción');
    }
  };

  const handleActionTypeSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      icon: formData.get('icon'),
      color: formData.get('color'),
      creates_quote: formData.get('creates_quote') === 'on'
    };

    try {
      if (editingActionType) {
        await axiosInstance.put(`/action-types/${editingActionType.id}`, data);
        toast.success('Tipo de acción actualizado');
      } else {
        await axiosInstance.post('/action-types', data);
        toast.success('Tipo de acción creado');
      }
      setActionDialogOpen(false);
      setEditingActionType(null);
      fetchActionTypes();
    } catch (error) {
      toast.error('Error al guardar tipo de acción');
    }
  };

  const handleDeleteActionType = async (id) => {
    if (!confirm('¿Estás seguro de eliminar este tipo de acción?')) return;
    
    try {
      await axiosInstance.delete(`/action-types/${id}`);
      toast.success('Tipo de acción eliminado');
      fetchActionTypes();
    } catch (error) {
      toast.error('Error al eliminar tipo de acción');
    }
  };

  const openEditActionTypeDialog = (actionType) => {
    setEditingActionType(actionType);
    setActionDialogOpen(true);
  };

  const openCreateActionTypeDialog = () => {
    setEditingActionType(null);
    setActionDialogOpen(true);
  };

  const statusColorOptions = [
    { value: 'blue', label: 'Azul' },
    { value: 'yellow', label: 'Amarillo' },
    { value: 'purple', label: 'Morado' },
    { value: 'orange', label: 'Naranja' },
    { value: 'green', label: 'Verde' },
    { value: 'red', label: 'Rojo' },
    { value: 'gray', label: 'Gris' }
  ];

  const actionColorOptions = [
    { value: 'blue', label: 'Azul', class: 'bg-blue-500' },
    { value: 'purple', label: 'Morado', class: 'bg-purple-500' },
    { value: 'green', label: 'Verde', class: 'bg-green-500' },
    { value: 'orange', label: 'Naranja', class: 'bg-orange-500' },
    { value: 'red', label: 'Rojo', class: 'bg-red-500' },
    { value: 'gray', label: 'Gris', class: 'bg-gray-500' }
  ];

  const iconOptions = [
    { value: 'Phone', label: 'Teléfono', Icon: Phone },
    { value: 'Monitor', label: 'Monitor/Demo', Icon: Monitor },
    { value: 'Users', label: 'Reunión', Icon: Users },
    { value: 'CheckSquare', label: 'Tarea', Icon: CheckSquare },
    { value: 'FileText', label: 'Documento', Icon: FileText }
  ];

  // ============= TICKET CATEGORIES =============
  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/ticket-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      color: formData.get('color'),
      description: formData.get('description')
    };

    try {
      if (editingCategory) {
        await axiosInstance.put(`/ticket-categories/${editingCategory.id}`, data);
        toast.success('Categoría actualizada');
      } else {
        await axiosInstance.post('/ticket-categories', data);
        toast.success('Categoría creada');
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error('Error al guardar categoría');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!(await confirmAsync('¿Eliminar esta categoría?'))) return;
    
    try {
      await axiosInstance.delete(`/ticket-categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setCategoryDialogOpen(true);
  };

  const openCreateCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryDialogOpen(true);
  };

  // ============= DEPARTMENTS =============
  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      toast.error('Error al cargar departamentos');
    }
  };

  const handleDepartmentSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      color: formData.get('color'),
      description: formData.get('description')
    };

    try {
      if (editingDepartment) {
        await axiosInstance.put(`/departments/${editingDepartment.id}`, data);
        toast.success('Departamento actualizado');
      } else {
        await axiosInstance.post('/departments', data);
        toast.success('Departamento creado');
      }
      setDepartmentDialogOpen(false);
      setEditingDepartment(null);
      fetchDepartments();
    } catch (error) {
      toast.error('Error al guardar departamento');
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (!(await confirmAsync('¿Eliminar este departamento?'))) return;
    
    try {
      await axiosInstance.delete(`/departments/${departmentId}`);
      toast.success('Departamento eliminado');
      fetchDepartments();
    } catch (error) {
      toast.error('Error al eliminar departamento');
    }
  };

  const openEditDepartmentDialog = (department) => {
    setEditingDepartment(department);
    setDepartmentDialogOpen(true);
  };

  const openCreateDepartmentDialog = () => {
    setEditingDepartment(null);
    setDepartmentDialogOpen(true);
  };

  // ============= TEAMUP CATEGORIES =============
  const fetchTeamupCategories = async () => {
    try {
      const response = await axiosInstance.get('/teamup/categories');
      setTeamupCategories(response.data);
    } catch (error) {
      console.error('Error loading teamup categories:', error);
    }
  };

  const handleSaveTeamupCategory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const categoryData = {
      name: formData.get('name'),
      color: formData.get('color'),
      description: formData.get('description') || '',
      order: parseInt(formData.get('order') || '0')
    };

    try {
      if (editingTeamupCategory) {
        await axiosInstance.put(`/teamup/categories/${editingTeamupCategory.id}`, categoryData);
        toast.success('Categoría actualizada');
      } else {
        await axiosInstance.post('/teamup/categories', categoryData);
        toast.success('Categoría creada');
      }
      setTeamupCategoryDialogOpen(false);
      fetchTeamupCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleDeleteTeamupCategory = async (categoryId) => {
    if (!(await confirmAsync('¿Eliminar esta categoría? No se puede eliminar si tiene eventos asociados.'))) return;
    
    try {
      await axiosInstance.delete(`/teamup/categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchTeamupCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const openEditTeamupCategoryDialog = (category) => {
    setEditingTeamupCategory(category);
    setTeamupCategoryDialogOpen(true);
  };

  const openCreateTeamupCategoryDialog = () => {
    setEditingTeamupCategory(null);
    setTeamupCategoryDialogOpen(true);
  };

  const categoryColorOptions = [
    { value: '#EF4444', label: 'Rojo', class: 'bg-red-500' },
    { value: '#F97316', label: 'Naranja', class: 'bg-orange-500' },
    { value: '#EAB308', label: 'Amarillo', class: 'bg-yellow-500' },
    { value: '#22C55E', label: 'Verde', class: 'bg-green-500' },
    { value: '#3B82F6', label: 'Azul', class: 'bg-blue-500' },
    { value: '#8B5CF6', label: 'Morado', class: 'bg-purple-500' },
    { value: '#EC4899', label: 'Rosa', class: 'bg-pink-500' },
    { value: '#6B7280', label: 'Gris', class: 'bg-gray-500' }
  ];

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-[#212B35] mb-2">Configuración CRM</h1>
          <p className="text-[#212B35]/70">Gestiona los estados del embudo y tipos de acciones</p>
        </div>

        <Tabs defaultValue="statuses" className="w-full">
          <TabsList className="grid w-full grid-cols-5 max-w-4xl">
            <TabsTrigger value="statuses">Estados del CRM</TabsTrigger>
            <TabsTrigger value="actions">Tipos de Acción</TabsTrigger>
            <TabsTrigger value="categories">Categorías Tickets</TabsTrigger>
            <TabsTrigger value="departments">Departamentos</TabsTrigger>
            <TabsTrigger value="teamup">Teamup</TabsTrigger>
          </TabsList>

          {/* ============= TAB: ESTADOS CRM ============= */}
          <TabsContent value="statuses" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Estados del embudo de ventas</p>
              <Button 
                onClick={openCreateStatusDialog}
                className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Estado
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                <DragDropContext onDragEnd={handleStatusDragEnd}>
                  <Droppable droppableId="statuses">
                    {(provided) => (
                      <div 
                        {...provided.droppableProps} 
                        ref={provided.innerRef}
                        className="space-y-2"
                      >
                        {statuses.map((status, index) => (
                          <Draggable key={status.id} draggableId={status.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center gap-4 p-4 border rounded-lg ${
                                  snapshot.isDragging ? 'bg-blue-50 shadow-lg' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div {...provided.dragHandleProps}>
                                  <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-semibold">{status.name}</span>
                                    <span className={`w-3 h-3 rounded-full bg-${status.color}-500`}></span>
                                    {status.convert_to_customer && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                        Convierte a Cliente
                                      </span>
                                    )}
                                    {status.create_support_ticket && (
                                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                        Crea Ticket
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">{status.description || 'Sin descripción'}</p>
                                  <p className="text-xs text-gray-400 mt-1">Clave: {status.key}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditStatusDialog(status)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteStatus(status.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: TIPOS DE ACCIÓN ============= */}
          <TabsContent value="actions" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Tipos de acciones comerciales disponibles</p>
              <Button 
                onClick={openCreateActionTypeDialog}
                className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Tipo
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {actionTypes.map((actionType) => {
                    const IconComponent = iconOptions.find(i => i.value === actionType.icon)?.Icon || Phone;
                    return (
                      <div
                        key={actionType.id}
                        className="p-4 border-2 rounded-lg hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg bg-${actionType.color}-100`}>
                              <IconComponent className={`w-5 h-5 text-${actionType.color}-600`} />
                            </div>
                            <span className="font-semibold">{actionType.name}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => openEditActionTypeDialog(actionType)}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteActionType(actionType.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: CATEGORÍAS TICKETS ============= */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Categorías para clasificar tickets de soporte</p>
              <Button 
                onClick={openCreateCategoryDialog}
                className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Categoría
              </Button>
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {categories.map((category) => (
                    <div 
                      key={category.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: category.color }}
                        />
                        <div>
                          <h4 className="font-medium">{category.name}</h4>
                          {category.description && (
                            <p className="text-sm text-gray-500">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditCategoryDialog(category)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No hay categorías creadas. Crea la primera categoría.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: DEPARTAMENTOS ============= */}
          <TabsContent value="departments" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Departamentos del sistema de tickets</p>
              <Button 
                onClick={openCreateDepartmentDialog}
                className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Departamento
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {departments.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No hay departamentos configurados
                    </div>
                  ) : (
                    departments.map((dept) => (
                      <div key={dept.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: dept.color }}
                          >
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#212B35]">{dept.name}</h3>
                            {dept.description && (
                              <p className="text-sm text-gray-600">{dept.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDepartmentDialog(dept)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteDepartment(dept.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: TEAMUP CATEGORIES ============= */}
          <TabsContent value="teamup">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Categorías de Teamup</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">Gestiona las categorías para eventos del calendario</p>
                </div>
                <Button onClick={openCreateTeamupCategoryDialog} className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Categoría
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {teamupCategories.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay categorías de Teamup configuradas</p>
                  ) : (
                    teamupCategories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-8 h-8 rounded"
                            style={{ backgroundColor: category.color }}
                          />
                          <div>
                            <h3 className="font-semibold">{category.name}</h3>
                            {category.description && (
                              <p className="text-sm text-gray-600">{category.description}</p>
                            )}
                            <p className="text-xs text-gray-500">Orden: {category.order}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditTeamupCategoryDialog(category)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteTeamupCategory(category.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ============= DIALOG: ESTADOS CRM ============= */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingStatus ? 'Editar Estado' : 'Nuevo Estado'}</DialogTitle>
              <DialogDescription>
                {editingStatus ? 'Modifica' : 'Crea'} un estado del embudo de ventas
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  name="name"
                  defaultValue={editingStatus?.name}
                  placeholder="ej: Nuevo, Contactado, Propuesta..."
                  required
                />
              </div>
              <div>
                <Label>Clave (única, sin espacios)</Label>
                <Input
                  name="key"
                  defaultValue={editingStatus?.key}
                  placeholder="Se autogenera del nombre si la dejas vacía"
                  pattern="[a-z0-9_]+"
                  disabled={!!editingStatus}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Identifica internamente la columna del kanban. Debe ser distinta para cada estado. Si la dejas vacía, se generará automáticamente.
                </p>
              </div>
              <div>
                <Label>Descripción (qué hacer en este estado)</Label>
                <Textarea
                  name="description"
                  defaultValue={editingStatus?.description}
                  placeholder="ej: Llamar al cliente en las próximas 24 horas..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Color</Label>
                <Select name="color" defaultValue={editingStatus?.color || 'gray'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusColorOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 p-4 border rounded-lg">
                <Checkbox 
                  id="convert_to_customer" 
                  name="convert_to_customer"
                  defaultChecked={editingStatus?.convert_to_customer || false}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="convert_to_customer"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Permitir Convertir a Cliente
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Mostrar botón "Convertir a Cliente" cuando el lead llegue a este estado
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 p-4 border rounded-lg">
                <Checkbox 
                  id="create_support_ticket" 
                  name="create_support_ticket"
                  defaultChecked={editingStatus?.create_support_ticket || false}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="create_support_ticket"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Crear Ticket de Soporte
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Crear automáticamente un ticket de soporte de prioridad alta cuando el lead llegue a este estado
                  </p>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white">
                {editingStatus ? 'Actualizar' : 'Crear'} Estado
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============= DIALOG: TIPOS DE ACCIÓN ============= */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingActionType ? 'Editar Tipo de Acción' : 'Nuevo Tipo de Acción'}</DialogTitle>
              <DialogDescription>
                {editingActionType ? 'Modifica' : 'Define'} un tipo de acción comercial
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleActionTypeSubmit} className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  name="name"
                  defaultValue={editingActionType?.name}
                  placeholder="ej: Llamada, Demo, Reunión..."
                  required
                />
              </div>
              <div>
                <Label>Icono</Label>
                <Select name="icon" defaultValue={editingActionType?.icon || 'Phone'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(option => {
                      const IconComp = option.Icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <IconComp className="w-4 h-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Select name="color" defaultValue={editingActionType?.color || 'blue'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {actionColorOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${option.class}`}></div>
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 p-4 border rounded-lg">
                <Checkbox 
                  id="creates_quote" 
                  name="creates_quote"
                  defaultChecked={editingActionType?.creates_quote || false}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="creates_quote"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Crear Presupuesto
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Al asignar esta acción, navegar a crear presupuesto automáticamente
                  </p>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white">
                {editingActionType ? 'Actualizar' : 'Crear'} Tipo de Acción
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============= DIALOG: CATEGORÍAS TICKETS ============= */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <Label htmlFor="category-name">Nombre *</Label>
                <Input
                  id="category-name"
                  name="name"
                  defaultValue={editingCategory?.name}
                  placeholder="Ej: Técnico, Facturación, Consulta"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category-description">Descripción</Label>
                <Textarea
                  id="category-description"
                  name="description"
                  defaultValue={editingCategory?.description}
                  placeholder="Descripción opcional de la categoría"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="category-color">Color *</Label>
                <Select name="color" defaultValue={editingCategory?.color || '#6B7280'}>
                  <SelectTrigger id="category-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryColorOptions.map(color => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${color.class}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                  {editingCategory ? 'Guardar Cambios' : 'Crear Categoría'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============= DIALOG: DEPARTAMENTOS ============= */}
        <Dialog open={departmentDialogOpen} onOpenChange={setDepartmentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDepartment ? 'Editar Departamento' : 'Nuevo Departamento'}</DialogTitle>
              <DialogDescription>
                {editingDepartment ? 'Modifica' : 'Crea'} un departamento para el sistema de tickets
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDepartmentSubmit} className="space-y-4">
              <div>
                <Label htmlFor="department-name">Nombre *</Label>
                <Input
                  id="department-name"
                  name="name"
                  defaultValue={editingDepartment?.name}
                  placeholder="Ej: Soporte, Desarrollo, Facturación"
                  required
                />
              </div>

              <div>
                <Label htmlFor="department-description">Descripción</Label>
                <Textarea
                  id="department-description"
                  name="description"
                  defaultValue={editingDepartment?.description}
                  placeholder="Descripción opcional del departamento"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="department-color">Color *</Label>
                <Select name="color" defaultValue={editingDepartment?.color || '#6B7280'}>
                  <SelectTrigger id="department-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryColorOptions.map(color => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${color.class}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDepartmentDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                  {editingDepartment ? 'Guardar Cambios' : 'Crear Departamento'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============= DIALOG: TEAMUP CATEGORIES ============= */}
        <Dialog open={teamupCategoryDialogOpen} onOpenChange={setTeamupCategoryDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingTeamupCategory ? 'Editar' : 'Nueva'} Categoría de Teamup
              </DialogTitle>
              <DialogDescription>
                Configura una categoría para organizar eventos del calendario
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveTeamupCategory} className="space-y-4">
              <div>
                <Label htmlFor="teamup-cat-name">Nombre *</Label>
                <Input
                  id="teamup-cat-name"
                  name="name"
                  defaultValue={editingTeamupCategory?.name}
                  placeholder="Ej: Instalaciones"
                  required
                />
              </div>

              <div>
                <Label htmlFor="teamup-cat-color">Color *</Label>
                <Select name="color" defaultValue={editingTeamupCategory?.color || '#3B82F6'}>
                  <SelectTrigger id="teamup-cat-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryColorOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${opt.class}`}></div>
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="teamup-cat-description">Descripción</Label>
                <Textarea
                  id="teamup-cat-description"
                  name="description"
                  defaultValue={editingTeamupCategory?.description}
                  placeholder="Descripción de la categoría (opcional)"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="teamup-cat-order">Orden</Label>
                <Input
                  id="teamup-cat-order"
                  name="order"
                  type="number"
                  defaultValue={editingTeamupCategory?.order || 0}
                  placeholder="0"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setTeamupCategoryDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                  {editingTeamupCategory ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
